import { } from 'react'
import { Link } from 'react-router-dom'
import { ConfigurationStatus } from '../components'
import { ArrowLeft, RefreshCw } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { useConfiguration } from '../hooks/useConfiguration'

export default function ConfigurationPage() {
  const configuration = useConfiguration()

  const handleRefresh = () => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  return (
    <>
      <Helmet>
        <title>API Configuration - FWO Pilot Portal</title>
        <meta name="description" content="API configuration status and troubleshooting" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <Link 
                to="/"
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Portal
              </Link>
              
              <button
                onClick={handleRefresh}
                className="inline-flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </button>
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900">API Configuration</h1>
            <p className="text-gray-600 mt-2">
              Diagnose and troubleshoot API connectivity issues
            </p>
          </div>

          {/* Configuration Status */}
          <div className="mb-8" id="configuration-status">
            <ConfigurationStatus />
          </div>

          {/* Environment Information */}
          <div className="bg-white rounded-lg border p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Environment Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Node Environment:</span>
                <span className="ml-2 text-gray-700">{import.meta.env.MODE}</span>
              </div>
              <div>
                <span className="font-medium">Build Time:</span>
                <span className="ml-2 text-gray-700">{new Date().toISOString()}</span>
              </div>
              <div>
                <span className="font-medium">User Agent:</span>
                <span className="ml-2 text-gray-700 break-all">
                  {typeof window !== 'undefined' ? window.navigator.userAgent : 'Server-side'}
                </span>
              </div>
            </div>
          </div>

          {/* Common Issues and Solutions */}
          <div className="bg-white rounded-lg border p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Common Issues and Solutions</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">🔧 Configuration Error</h3>
                <p className="text-sm text-gray-600 mb-2">
                  The API endpoint is not properly configured or is using a placeholder URL.
                </p>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <p className="font-medium mb-1">Solution:</p>
                  <ol className="space-y-1 ml-4">
                    <li>1. Run: <code className="bg-gray-200 px-1 rounded">npm run extract-api-config</code></li>
                    <li>2. Verify your Terragrunt deployment: <code className="bg-gray-200 px-1 rounded">cd infrastructure/live/dev && terragrunt output</code></li>
                    <li>3. Restart the development server: <code className="bg-gray-200 px-1 rounded">npm run dev</code></li>
                  </ol>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">🌐 Network Connectivity</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Unable to reach the API server due to network issues.
                </p>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <p className="font-medium mb-1">Solution:</p>
                  <ul className="space-y-1 ml-4">
                    <li>• Check your internet connection</li>
                    <li>• Verify the API Gateway is deployed and accessible</li>
                    <li>• Check for firewall or proxy issues</li>
                    <li>• Try accessing the API URL directly in your browser</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">🚫 CORS Issues</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Cross-origin requests are being blocked by the browser.
                </p>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <p className="font-medium mb-1">Solution:</p>
                  <ul className="space-y-1 ml-4">
                    <li>• Verify API Gateway CORS configuration</li>
                    <li>• Ensure the frontend domain is whitelisted</li>
                    <li>• Check that preflight requests are handled correctly</li>
                    <li>• Contact your system administrator</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">🔐 Authentication Problems</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Issues with login credentials or token validation.
                </p>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <p className="font-medium mb-1">Solution:</p>
                  <ul className="space-y-1 ml-4">
                    <li>• Clear browser storage and try logging in again</li>
                    <li>• Request new login credentials</li>
                    <li>• Verify your email is registered in the system</li>
                    <li>• Check that Cognito User Pool is properly configured</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Developer Tools */}
          {configuration?.isDevelopmentMode && (
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-xl font-semibold mb-4">Developer Tools</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Environment Variables</h3>
                  <div className="bg-gray-50 p-3 rounded text-sm font-mono">
                    <div>VITE_API_BASE_URL: {import.meta.env.VITE_API_BASE_URL || 'undefined'}</div>
                    <div>VITE_AWS_REGION: {import.meta.env.VITE_AWS_REGION || 'undefined'}</div>
                    <div>MODE: {import.meta.env.MODE}</div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Quick Actions</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        if (typeof window !== 'undefined') {
                          localStorage.clear()
                          sessionStorage.clear()
                          window.location.reload()
                        }
                      }}
                      className="block px-4 py-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 transition-colors"
                    >
                      Clear All Storage & Reload
                    </button>
                    
                    <button
                      onClick={() => {
                        console.log('Configuration:', configuration)
                        alert('Configuration logged to console')
                      }}
                      className="block px-4 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                    >
                      Log Configuration to Console
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}